/* eslint-disable @next/next/no-img-element */
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { BlogPost } from '@/data/blog'

import { Layout, Seo, PageHero } from '@/components/generals'
import { BlogCard } from '@/components/blog'
import { routes } from '@/lib/routes'
import { getBlogPosts, getPostBySlug } from '@/data/blog'

type PostPageProps = {
    post: BlogPost
    related: BlogPost[]
}

const PostPage: FC<PostPageProps> = ({ post, related }): ReactNode => {
    return (
        <Layout>
            <Seo title={post.name} description={post.summary} ogImage={post.mainImage} />

            <PageHero
                badge='Category'
                eyebrow={post.categoryName}
                eyebrowHref={routes.blogCategory(post.category)}
                title={post.name}
                description={post.summary}
            />

            <div className='section'>
                <div className='container'>
                    <div className='fade-in-on-scroll'>
                        <img src={post.mainImage} loading='lazy' alt={post.name} className='main-blog-image' />
                    </div>
                    <div className='rich-text-container'>
                        <div className='rich-text-holder'>
                            <div className='rich-text w-richtext' dangerouslySetInnerHTML={{ __html: post.body }} />
                        </div>
                    </div>
                </div>
            </div>

            {related.length > 0 && (
                <div className='section'>
                    <div className='container'>
                        <div className='fade-in-on-scroll'>
                            <div className='text-container'>
                                <h3>Read more related articles</h3>
                                <div className='grey-text'>More ways to organize your time, finances, and tasks with San Diego.</div>
                            </div>
                        </div>
                        <div className='blog-items-holder'>
                            <div className='cms-wrapper'>
                                <div role='list' className='blog-grid w-dyn-items'>
                                    {related.map(item => (
                                        <BlogCard key={item.slug} post={item} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const posts = await getBlogPosts()
    return {
        paths: posts.map(post => ({ params: { slug: post.slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<PostPageProps> = async ({ params }) => {
    const slug = params?.slug as string
    const post = await getPostBySlug(slug)

    if (!post) {
        return { notFound: true }
    }

    const all = await getBlogPosts()
    const related = all
        .filter(item => item.slug !== post.slug)
        .sort((a, b) => (a.category === post.category ? -1 : 1) - (b.category === post.category ? -1 : 1))
        .slice(0, 3)

    return { props: { post, related }, revalidate: 60 }
}

export default PostPage
